/**
 * Mod Client — activare în Interfață Client + statistici modul activ.
 */
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=83.21';
import { saveCampaignSettingsBackend, saveLoyaltySettingsBackend } from '../services/backend.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import { invalidateLoyaltySettingsCache } from './loyalty-module-admin.js?v=83.21';
import {
  DEFAULT_LOYALTY_TITLE,
  DEFAULT_LOYALTY_DESCRIPTION,
  DEFAULT_LOYALTY_BUTTON,
  DEFAULT_LOYALTY_REQUIRED,
  DEFAULT_LOYALTY_REWARD_TEXT,
  loyaltyFormHasUnsavedChanges,
  saveLoyaltySettings
} from './loyalty-module-admin.js?v=83.21';
import {
  DEFAULT_EVENT_BUTTON_LABEL,
  DEFAULT_EVENT_TITLE,
  eventFormHasUnsavedChanges,
  saveEventModuleSettings
} from './event-module-admin.js?v=83.21';
import { getBucharestDateKey } from '../utils/helpers.js?v=83.21';
import { isLoyaltyVisible } from './loyalty-module.js?v=83.21';
import { isEventVisible } from './event-module.js?v=83.21';
import { CLIENT_TEMPLATE_LABELS, normalizeClientTemplateStyle } from './client-template-style.js?v=83.21';
import { getLoyaltyMembersCache, loadLoyaltyMembersCache } from '../services/loyalty.js?v=83.21';
import { getLastEventMessagesRows } from '../services/event-messages.js?v=83.21';
import { getClientInterfaceAnalyticsBackend } from '../services/backend.js?v=83.21';

const MODULE_IDS = ['campanii', 'fidelizare', 'evenimente'];

const MODULE_LABELS = {
  campanii: 'Campanie',
  fidelizare: 'Fidelizare',
  evenimente: 'Evenimente'
};

async function refreshAllAdminSummaries() {
  const { updateSectionSummaries } = await import('./admin-collapsible.js?v=83.21');
  updateSectionSummaries();
}

function getCampaignState(settings) {
  const status = settings?.campaignStatus || 'draft';
  const hasAdminPass = true; // PIN-ul nu mai e necesar
  return {
    status,
    isActive: status === 'active',
    isClosed: status === 'closed' || status === 'inactive',
    hasAdminPass
  };
}

function getLoyaltyState(settings) {
  const ls = settings?.loyaltySettings;
  const active = ls?.active === true;
  const campaignActive = (settings?.campaignStatus || 'draft') === 'active';
  return { active, campaignActive, configured: !!ls, visibleOnClient: isLoyaltyVisible(settings) };
}

function getEventState(settings) {
  const em = settings?.eventModule;
  const enabled = em?.enabled === true;
  const visibleOnClient = isEventVisible(settings);
  const imageCount = Array.isArray(em?.images)
    ? em.images.filter((u) => String(u || '').trim()).length
    : 0;
  return { enabled, visibleOnClient, imageCount };
}

/** Modul activ în setări (maxim unul). */
function getActiveClientModuleId(settings) {
  const camp = getCampaignState(settings);
  if (camp.isActive) return 'campanii';
  const fid = getLoyaltyState(settings);
  if (fid.active) return 'fidelizare';
  const ev = getEventState(settings);
  if (ev.enabled) return 'evenimente';
  return null;
}

function getModuleActivateBlockReason(moduleId, settings) {
  const activeId = getActiveClientModuleId(settings);
  if (!activeId || activeId === moduleId) return null;
  const activeLabel = MODULE_LABELS[activeId] || activeId;
  return `Acum rulează ${activeLabel}. Pe pagină poate merge o singură activitate odată.`;
}

const SWITCH_LABELS = { campanii: 'Campania', fidelizare: 'Fidelizarea', evenimente: 'Evenimentul' };
const SWITCH_SHORT = { campanii: 'Campanie', fidelizare: 'Fidelizare', evenimente: 'Eveniment' };

/** Fereastră de confirmare (în locul erorii roșii). Rezolvă true / false. */
function askConfirm({ title, text, confirm, cancel }) {
  return new Promise((resolve) => {
    document.getElementById('sjSwitchDialog')?.remove();
    const wrap = document.createElement('div');
    wrap.id = 'sjSwitchDialog';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    const card = document.createElement('div');
    card.className = 'sjsd-card';
    const h = document.createElement('h2');
    h.className = 'sjsd-title';
    h.textContent = title;
    const p = document.createElement('p');
    p.className = 'sjsd-text';
    p.textContent = text;
    const row = document.createElement('div');
    row.className = 'sjsd-row';
    const no = document.createElement('button');
    no.type = 'button';
    no.className = 'sjsd-btn sjsd-btn--ghost';
    no.textContent = cancel || 'Renunț';
    const yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'sjsd-btn sjsd-btn--main';
    yes.textContent = confirm || 'Da';
    row.append(no, yes);
    card.append(h, p, row);
    wrap.append(card);
    const done = (v) => {
      wrap.remove();
      resolve(v);
    };
    no.addEventListener('click', () => done(false));
    yes.addEventListener('click', () => done(true));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(false); });
    document.body.appendChild(wrap);
    setTimeout(() => yes.focus(), 30);
  });
}

/**
 * Altă activitate rulează deja: întrebăm frumos dacă schimbăm.
 * Fidelizarea / evenimentul se opresc direct (datele rămân); campania se oprește prin fereastra ei (arhivare).
 * Întoarce true dacă drumul e liber pentru `targetId`.
 */
async function resolveModuleConflict(targetId, settings) {
  const activeId = getActiveClientModuleId(settings);
  if (!activeId || activeId === targetId) return true;
  const from = SWITCH_LABELS[activeId];
  const to = SWITCH_LABELS[targetId];
  if (activeId === 'campanii') {
    const ok = await askConfirm({
      title: `Pornești ${to.toLowerCase()}?`,
      text: `Acum rulează o campanie. Pe pagină poate merge o singură activitate odată, așa că mai întâi oprim campania — revendicările rămân salvate în arhivă. După ce o oprești, apasă din nou pe „Activează”.`,
      confirm: 'Oprește campania',
      cancel: 'Rămân pe campanie'
    });
    if (ok) {
      const { openStopCampaignModal } = await import('../services/admin.js?v=83.21');
      openStopCampaignModal();
    }
    return false;
  }
  const keep = activeId === 'fidelizare'
    ? 'Clienții își păstrează ștampilele și le poți reporni oricând.'
    : 'Mesajele primite rămân salvate.';
  const ok = await askConfirm({
    title: `Schimbi pe ${SWITCH_SHORT[targetId]}?`,
    text: `Acum rulează ${from.toLowerCase()}. Pe pagină poate merge o singură activitate odată, așa că oprim ${from.toLowerCase()} și pornim ${to.toLowerCase()}. ${keep}`,
    confirm: `Da, schimbă pe ${SWITCH_SHORT[targetId]}`,
    cancel: `Rămân pe ${SWITCH_SHORT[activeId]}`
  });
  if (!ok) return false;
  try {
    if (activeId === 'fidelizare') await setLoyaltyModuleActiveFromModClient(false, { quiet: true });
    else if (activeId === 'evenimente') await setEventModuleActiveFromModClient(false, { quiet: true });
  } catch (_) {
    return false;
  }
  return !getModuleActivateBlockReason(targetId, getCurrentSettings() || {});
}

const MODULE_TOGGLE_BUTTON_IDS = {
  campanii: 'moduleActivateCampanii',
  fidelizare: 'moduleActivateFidelizare',
  evenimente: 'moduleActivateEvenimente'
};

const moduleToggleBusy = new Set();

function getModuleToggleButtons(moduleId) {
  const btnId = MODULE_TOGGLE_BUTTON_IDS[moduleId];
  return btnId ? Array.from(document.querySelectorAll(`#${btnId}`)) : [];
}

function setModuleToggleLoading(moduleId, loading) {
  getModuleToggleButtons(moduleId).forEach((btn) => {
    if (loading) {
      if (!btn.dataset.toggleBusy) {
        btn.dataset.toggleBusy = '1';
      }
      btn.disabled = true;
      btn.textContent = 'Se încarcă…';
      btn.classList.add('mod-client-module__toggle--busy');
    } else if (btn.dataset.toggleBusy) {
      btn.classList.remove('mod-client-module__toggle--busy');
      delete btn.dataset.toggleBusy;
    }
  });
}

/** Loading pe butoanele Activare/Dezactivare din Mod Client (inclusiv duplicate în modale). */
export async function withModClientModuleToggleBusy(moduleId, fn) {
  const id = String(moduleId || '').toLowerCase();
  if (!MODULE_TOGGLE_BUTTON_IDS[id]) return fn();
  if (moduleToggleBusy.has(id)) return;
  moduleToggleBusy.add(id);
  setModuleToggleLoading(id, true);
  try {
    return await fn();
  } finally {
    moduleToggleBusy.delete(id);
    setModuleToggleLoading(id, false);
    updateModuleCardActivationButtons(getCurrentSettings());
  }
}

function buildCampaniiMeta(settings) {
  const { status, isActive, isClosed, hasAdminPass } = getCampaignState(settings);
  const totalTries = Number(settings?.totalTries);
  const tries = Number.isFinite(totalTries) && totalTries >= 0 ? totalTries : 0;
  const prizeCount = Array.isArray(settings?.prizes) ? settings.prizes.length : 0;

  let statusLabel = 'Inactiv';
  let hint = 'Pornește campania pentru joc și premii.';
  if (isActive) {
    statusLabel = 'Activ';
    hint = `${tries} încercări · ${prizeCount} recompense`;
  } else if (isClosed) {
    statusLabel = 'Închisă';
    hint = hasAdminPass
      ? 'Campanie încheiată. Apasă butonul pentru a porni una nouă.'
      : 'Campanie încheiată. Setează PIN-ul, apoi pornește campania.';
  } else if (!hasAdminPass) {
    hint = 'Setează PIN-ul de securitate înainte de activare.';
  }

  return { isActive, isClosed, hasAdminPass, statusLabel, hint, tries, prizeCount };
}

function buildFidelizareMeta(settings) {
  const { active, campaignActive, visibleOnClient } = getLoyaltyState(settings);
  const req = settings?.loyaltySettings?.requiredPurchases ?? DEFAULT_LOYALTY_REQUIRED;
  const lsd = settings?.loyaltySettings || {};
  const todayKey = getBucharestDateKey();
  const expired = !!(lsd.endDate && String(lsd.endDate) < todayKey);
  const notStarted = !!(lsd.startDate && String(lsd.startDate) > todayKey);
  if (active && (expired || notStarted)) {
    return {
      active,
      visibleOnClient: false,
      statusLabel: expired ? 'Expirat' : 'Programat',
      hint: expired
        ? 'Perioada s-a încheiat — apasă „Repornește fidelizarea” ca să-l pornești din nou fără dată de final.'
        : `Pornește pe ${String(lsd.startDate).split('-').reverse().join('.')}.`
    };
  }
  let statusLabel = 'Inactiv';
  if (active && visibleOnClient) {
    statusLabel = 'Activ';
  } else if (active && campaignActive) {
    statusLabel = 'Activ · ascuns';
  } else if (active) {
    statusLabel = 'Activ';
  }
  let hint = 'Activează pentru înregistrare achiziții.';
  if (active && visibleOnClient) {
    hint = `Vizibil pe client · ${req} achiziții / recompensă`;
  } else if (active && campaignActive) {
    hint = 'Pornit în setări · ascuns cât campania rulează';
  } else if (active) {
    hint = `Program activ · ${req} achiziții / recompensă`;
  }
  return { active, visibleOnClient, statusLabel, hint };
}

function buildEvenimenteMeta(settings) {
  const { enabled, visibleOnClient, imageCount } = getEventState(settings);
  const fid = getLoyaltyState(settings);
  const camp = getCampaignState(settings);
  let statusLabel = 'Inactiv';
  if (enabled && visibleOnClient) {
    statusLabel = 'Activ';
  } else if (enabled && (camp.isActive || fid.active)) {
    statusLabel = 'Activ · ascuns';
  } else if (enabled) {
    statusLabel = 'Activ';
  }
  let hint = 'Activează pentru evenimente speciale.';
  if (enabled && visibleOnClient) {
    hint =
      imageCount > 0
        ? `${imageCount} imagini · mesaje invitați`
        : 'Modul activ · mesaje invitați';
  } else if (enabled && camp.isActive) {
    hint = 'Pornit în setări · ascuns cât campania rulează';
  } else if (enabled && fid.active) {
    hint = 'Pornit în setări · ascuns cât fidelizarea rulează';
  } else if (enabled) {
    hint = imageCount > 0 ? `${imageCount} imagini · mesaje invitați` : 'Modul activ · mesaje invitați';
  }
  return { enabled, visibleOnClient, statusLabel, hint };
}

function setToggleButton(btn, { active, activateLabel, deactivateLabel, disabled, disabledReason }) {
  if (!btn) return;
  if (btn.dataset.toggleBusy) return;
  btn.disabled = !!disabled;
  if (disabledReason) {
    btn.title = disabledReason;
  } else {
    btn.removeAttribute('title');
  }
  btn.textContent = active ? deactivateLabel : activateLabel;
  btn.classList.toggle('mod-client-module__toggle--off', active);
  btn.classList.toggle('mod-client-module__toggle--on', !active);
}

function setActivationBlockBadge(prefix, statusLabel, hint, isOn) {
  const badge = document.getElementById(`moduleActivate${prefix}Badge`);
  if (badge) {
    badge.textContent = statusLabel || (isOn ? 'Activ' : 'Inactiv');
    badge.classList.toggle('ci-module-activate-block__badge--on', !!isOn);
    badge.classList.toggle('ci-module-activate-block__badge--off', !isOn);
  }
  const hintEl = document.getElementById(`moduleActivate${prefix}Hint`);
  if (hintEl && hint != null) {
    hintEl.textContent = hint;
  }
}

function updateModuleCardActivationButtons(settings) {
  const s = settings || getCurrentSettings();
  if (!s) return;

  const camp = buildCampaniiMeta(s);
  const fid = buildFidelizareMeta(s);
  const ev = buildEvenimenteMeta(s);

  const blockCamp = getModuleActivateBlockReason('campanii', s);
  const blockFid = getModuleActivateBlockReason('fidelizare', s);
  const blockEv = getModuleActivateBlockReason('evenimente', s);

  setActivationBlockBadge('Campanii', camp.statusLabel, !camp.isActive && blockCamp ? blockCamp : camp.hint, camp.isActive);
  setActivationBlockBadge('Fidelizare', fid.statusLabel, !fid.active && blockFid ? blockFid : fid.hint, fid.active);
  setActivationBlockBadge('Evenimente', ev.statusLabel, !ev.enabled && blockEv ? blockEv : ev.hint, ev.enabled);

  getModuleToggleButtons('campanii').forEach((btn) =>
    setToggleButton(btn, {
      active: camp.isActive,
      activateLabel: blockCamp ? 'Schimbă pe Campanie' : camp.isClosed ? 'Pornește campanie nouă' : 'Activează campania',
      deactivateLabel: 'Dezactivează campania',
      disabled: !camp.isActive && !camp.hasAdminPass,
      disabledReason: !camp.hasAdminPass
        ? 'Setează mai întâi PIN-ul de securitate'
        : !camp.isActive && blockCamp
          ? blockCamp
          : undefined
    })
  );

  const fidExpired = fid.active && fid.statusLabel === 'Expirat';
  getModuleToggleButtons('fidelizare').forEach((btn) =>
    setToggleButton(btn, {
      active: fid.active && !fidExpired,
      activateLabel: fidExpired ? 'Repornește fidelizarea' : blockFid ? 'Schimbă pe Fidelizare' : 'Activează fidelizarea',
      deactivateLabel: 'Dezactivează fidelizarea',
      disabled: false,
      disabledReason: !fid.active && blockFid ? blockFid : undefined
    })
  );

  getModuleToggleButtons('evenimente').forEach((btn) =>
    setToggleButton(btn, {
      active: ev.enabled,
      activateLabel: blockEv ? 'Schimbă pe Eveniment' : 'Activează evenimente',
      deactivateLabel: 'Dezactivează evenimente',
      disabled: false,
      disabledReason: !ev.enabled && blockEv ? blockEv : undefined
    })
  );
}

function getThemeLabel(settings) {
  const raw = normalizeClientTemplateStyle(settings?.clientTemplateStyle);
  return CLIENT_TEMPLATE_LABELS[raw] || raw.charAt(0).toUpperCase() + raw.slice(1);
}

function statRow(label, value, muted = false) {
  const valClass = muted ? 'mod-client-stat-value muted' : 'mod-client-stat-value';
  return `<div class="mod-client-stat-row"><span class="mod-client-stat-label">${label}</span><span class="${valClass}">${value}</span></div>`;
}

function renderStatsRows(rows) {
  return rows.map(([label, value, muted]) => statRow(label, value, !!muted)).join('');
}

function updateHiddenCampaignTries(settings) {
  const totalTriesRaw = Number(settings?.totalTries);
  const totalTries = Number.isFinite(totalTriesRaw) && totalTriesRaw >= 0 ? totalTriesRaw : 0;
  const remainingTriesRaw = Number(settings?.remainingTries);
  const remainingTries =
    Number.isFinite(remainingTriesRaw) && remainingTriesRaw >= 0 ? remainingTriesRaw : 0;

  const hiddenTotal = document.getElementById('modClientTotalTries');
  if (hiddenTotal) hiddenTotal.textContent = totalTries > 0 ? String(totalTries) : '—';

  const hiddenRem = document.getElementById('modClientRemainingTries');
  if (hiddenRem) {
    hiddenRem.textContent =
      totalTries > 0 ? String(Math.max(0, remainingTries)) : '—';
  }
}

function renderCampaignStats(settings) {
  const totalTriesRaw = Number(settings?.totalTries);
  const totalTries = Number.isFinite(totalTriesRaw) && totalTriesRaw >= 0 ? totalTriesRaw : 0;
  const remainingTriesRaw = Number(settings?.remainingTries);
  const remainingTries =
    Number.isFinite(remainingTriesRaw) && remainingTriesRaw >= 0 ? remainingTriesRaw : 0;

  const allPrizes = Array.isArray(settings?.prizes) ? settings.prizes : [];
  const totalPrizesAvailable = allPrizes.reduce((sum, p) => {
    const count = Number(p.count);
    return sum + (Number.isFinite(count) && count >= 0 ? count : 0);
  }, 0);
  const prizeImgCount = allPrizes.filter((p) => p.image && String(p.image).trim()).length;
  const bgCount = Array.isArray(settings?.bgURLs) ? settings.bgURLs.length : 0;

  const triesDisplay =
    totalTries > 0 ? `${Math.max(0, remainingTries)} / ${totalTries}` : '—';

  return renderStatsRows([
    ['Încercări rămase', triesDisplay, totalTries <= 0],
    ['Recompense disponibile', String(totalPrizesAvailable), totalPrizesAvailable <= 0],
    ['Tipuri de recompense', String(allPrizes.length), allPrizes.length <= 0],
    ['Fundaluri configurate', String(bgCount), bgCount <= 0],
    ['Imagini recompense', String(prizeImgCount), prizeImgCount <= 0],
    ['Tema selectată', getThemeLabel(settings), false]
  ]);
}

function renderLoyaltyStats(settings) {
  const members = getLoyaltyMembersCache();
  const enrolled = members.length;
  const purchases = members.reduce((sum, m) => sum + (Number(m.purchaseCount) || 0), 0);
  const rewardsPending = members.filter(
    (m) =>
      m?.rewardAvailable === true ||
      String(m?.status || '').toLowerCase().includes('disponibil')
  ).length;
  const rewardsClaimed = members.reduce(
    (sum, m) => sum + (Number(m.rewardsClaimedCount) || 0),
    0
  );
  const rewardsConfigured = rewardsPending > 0 ? rewardsPending : rewardsClaimed;
  const reqRaw = Number(settings?.loyaltySettings?.requiredPurchases);
  const required =
    Number.isFinite(reqRaw) && reqRaw >= 1 && reqRaw <= 99 ? reqRaw : 7;

  return renderStatsRows([
    ['Clienți înscriși', String(enrolled), enrolled <= 0],
    ['Achiziții înregistrate', String(purchases), purchases <= 0],
    ['Premii configurate', String(rewardsConfigured), rewardsConfigured <= 0],
    ['Prag fidelizare', `${required} achiziții`, false],
    ['Tema selectată', getThemeLabel(settings), false]
  ]);
}

function renderEventStats(settings, eventInteractions = 0) {
  const messages = getLastEventMessagesRows();
  const messageCount = messages.length;
  const participants = eventInteractions > 0 ? eventInteractions : messageCount;
  const images = Array.isArray(settings?.eventModule?.images)
    ? settings.eventModule.images.filter((u) => String(u || '').trim()).length
    : 0;

  return renderStatsRows([
    ['Participanți', String(participants), participants <= 0],
    ['Mesaje primite', String(messageCount), messageCount <= 0],
    ['Poze eveniment', String(images), images <= 0],
    ['Tema selectată', getThemeLabel(settings), false]
  ]);
}

async function updateModClientStatsPanel(settings) {
  const titleEl = document.getElementById('modClientStatsTitle');
  const bodyEl = document.getElementById('modClientStatsBody');
  if (!titleEl || !bodyEl) return;

  updateHiddenCampaignTries(settings);

  const activeId = getActiveClientModuleId(settings);
  const hiddenStatus = document.getElementById('modClientCampaignStatus');
  if (hiddenStatus) {
    hiddenStatus.textContent = activeId
      ? MODULE_LABELS[activeId] || activeId
      : 'Inactiv';
  }

  if (!activeId) {
    titleEl.textContent = 'Statistici';
    bodyEl.innerHTML =
      '<p class="mod-client-stats-empty smallnote">Niciun modul activ. Activează Campanie, Fidelizare sau Evenimente din Interfață Client.</p>';
    return;
  }

  if (activeId === 'campanii') {
    titleEl.textContent = 'Statistici campanie';
    bodyEl.innerHTML = renderCampaignStats(settings);
    return;
  }

  if (activeId === 'fidelizare') {
    titleEl.textContent = 'Statistici fidelizare';
    if (getLoyaltyMembersCache().length === 0) {
      await loadLoyaltyMembersCache();
    }
    bodyEl.innerHTML = renderLoyaltyStats(settings);
    return;
  }

  if (activeId === 'evenimente') {
    titleEl.textContent = 'Statistici eveniment';
    let eventInteractions = 0;
    try {
      const data = await getClientInterfaceAnalyticsBackend('total');
      const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
      const ev = metrics.find((m) => m.key === 'event');
      eventInteractions = Number(ev?.value) || 0;
    } catch (_) {
      /* ignore */
    }
    bodyEl.innerHTML = renderEventStats(settings, eventInteractions);
  }
}

export function updateModClientModulesUI(settings) {
  const s = settings || getCurrentSettings();
  if (!s) return;

  updateModuleCardActivationButtons(s);

  const enterBtn = document.getElementById('enterClientBtn');
  if (enterBtn) {
    enterBtn.style.display = 'inline-flex';
    enterBtn.disabled = false;
  }
  const startBtn = document.getElementById('startCampaignBtn');
  if (startBtn) startBtn.style.display = 'none';

  updateModClientStatsPanel(s);
}

/**
 * Campanie rapidă: salvează premii + jocuri + perioadă (+ PIN dacă lipsește) și pornește campania.
 * opts.start=false → doar salvează.
 */
/** Ca quickSaveAndStartCampaign, dar pornește direct (fără fereastra de confirmare) — doar pentru campanii sigure (guaranteed). */
export async function saveAndStartCampaignDirect(payload) {
  let s = getCurrentSettings() || {};
  if (getModuleActivateBlockReason('campanii', s)) {
    const free = await resolveModuleConflict('campanii', s);
    if (!free) return false;
    s = getCurrentSettings() || {};
  }
  const [{ saveCampaignSettingsBackend }, settingsMod, authMod, adminMod] = await Promise.all([
    import('../services/backend.js?v=83.21'),
    import('../services/settings.js?v=83.21'),
    import('../services/auth.js?v=83.21'),
    import('../services/admin.js?v=83.21')
  ]);
  await saveCampaignSettingsBackend(payload);
  const uid = authMod.getCurrentUser?.()?.uid;
  if (uid) { try { await settingsMod.loadSettings(uid, true); } catch (_) { /* ignore */ } }
  await withModClientModuleToggleBusy('campanii', () => adminMod.startCampaign(null));
  try { const { applyClientUI } = await import('./slideshow.js?v=83.21'); applyClientUI(); } catch (_) { /* ignore */ }
  updateModClientModulesUI(getCurrentSettings());
  try { await refreshAllAdminSummaries(); } catch (_) { /* ignore */ }
  return true;
}

export async function quickSaveAndStartCampaign(payload, opts = {}) {
  const start = opts.start !== false;
  let s = getCurrentSettings() || {};
  if (start && getModuleActivateBlockReason('campanii', s)) {
    const free = await resolveModuleConflict('campanii', s);
    if (!free) return false;
    s = getCurrentSettings() || {};
  }
  const [{ saveCampaignSettingsBackend }, settingsMod, authMod] = await Promise.all([
    import('../services/backend.js?v=83.21'),
    import('../services/settings.js?v=83.21'),
    import('../services/auth.js?v=83.21')
  ]);
  await saveCampaignSettingsBackend(payload);
  const uid = authMod.getCurrentUser?.()?.uid;
  if (uid) {
    try { await settingsMod.loadSettings(uid, true); } catch (_) { /* ignore */ }
  }
  if (start) {
    // pornirea trece prin fereastra cu confirmarea notarului (campanie la noroc)
    const { openStartCampaignModal } = await import('../services/admin.js?v=83.21');
    openStartCampaignModal();
  }
  try {
    const { applyClientUI } = await import('./slideshow.js?v=83.21');
    applyClientUI();
  } catch (_) { /* ignore */ }
  updateModClientModulesUI(getCurrentSettings());
  await refreshAllAdminSummaries();
  return true;
}

export async function toggleModClientCampanii() {
  const s = getCurrentSettings();
  if (!s) return;
  const camp = getCampaignState(s);
  if (camp.isActive) {
    const { openStopCampaignModal } = await import('../services/admin.js?v=83.21');
    openStopCampaignModal();
    return;
  }
  if (getModuleActivateBlockReason('campanii', s)) {
    const free = await resolveModuleConflict('campanii', s);
    if (!free) return;
  }
  if (!camp.hasAdminPass) {
    showSnackbar('Setează PIN-ul de securitate înainte de a porni campania.', 'error');
    return;
  }
  const { openStartCampaignModal } = await import('../services/admin.js?v=83.21');
  openStartCampaignModal();
}

export async function setLoyaltyModuleActiveFromModClient(active, opts = {}) {
  let s = getCurrentSettings() || {};
  if (active && getModuleActivateBlockReason('fidelizare', s)) {
    const free = await resolveModuleConflict('fidelizare', s);
    if (!free) return;
    s = getCurrentSettings() || {};
  }
  // Modificările nesalvate din formular se salvează întâi — „Activează” pornește exact ce vezi.
  if (active && loyaltyFormHasUnsavedChanges()) {
    try {
      await saveLoyaltySettings({ quiet: true });
    } catch (e) {
      showSnackbar(e?.message || 'Nu am putut salva modificările.', 'error');
      if (opts.quiet) throw e;
      return;
    }
    s = getCurrentSettings() || {};
  }
  const ls = s.loyaltySettings || {};
  let endDate = String(ls.endDate || '').trim();
  let clearedExpired = false;
  if (active && endDate && endDate < getBucharestDateKey()) {
    endDate = '';
    clearedExpired = true;
  }
  const payload = {
    active: !!active,
    title: String(ls.title || '').trim() || DEFAULT_LOYALTY_TITLE,
    description: String(ls.description || '').trim() || DEFAULT_LOYALTY_DESCRIPTION,
    buttonText: String(ls.buttonText || '').trim() || DEFAULT_LOYALTY_BUTTON,
    requiredPurchases:
      Number(ls.requiredPurchases) >= 1 && Number(ls.requiredPurchases) <= 99
        ? Number(ls.requiredPurchases)
        : DEFAULT_LOYALTY_REQUIRED,
    rewardText: String(ls.rewardText || '').trim() || DEFAULT_LOYALTY_REWARD_TEXT,
    promoImageUrl: ls.promoImageUrl || null,
    startDate: String(ls.startDate || '').trim(),
    endDate,
    minAmount: Math.max(0, Number(ls.minAmount) || 0)
  };

  try {
    invalidateLoyaltySettingsCache();
    const saved = await saveLoyaltySettingsBackend(payload);
    const merged = saved?.settings || payload;
    setCurrentSettings({ ...s, loyaltySettings: merged });

    const { applyClientUI } = await import('./slideshow.js?v=83.21');
    applyClientUI();
    updateModClientModulesUI(getCurrentSettings());
    await refreshAllAdminSummaries();

    if (clearedExpired) {
      const endInput = document.getElementById('loyaltyEndDate');
      if (endInput) endInput.value = '';
      document.getElementById('loyaltyPeriodNote')?.setAttribute('hidden', '');
    }
    if (!opts.quiet) {
      showSnackbar(
        active
          ? clearedExpired
            ? 'Fidelizarea e activă. Am scos data de final expirată.'
            : 'Fidelizarea este activă pe client.'
          : 'Fidelizarea a fost dezactivată.',
        'success'
      );
    }
  } catch (e) {
    showSnackbar(e?.message || 'Nu am putut actualiza fidelizarea.', 'error');
    updateModClientModulesUI(getCurrentSettings());
    if (opts.quiet) throw e;
  }
}

export async function toggleModClientFidelizare() {
  await withModClientModuleToggleBusy('fidelizare', async () => {
    await refreshModClientModulesAsync();
    const s = getCurrentSettings();
    if (!s) return;
    const { active } = getLoyaltyState(s);
    const lsd = s.loyaltySettings || {};
    const expired = !!(lsd.endDate && String(lsd.endDate) < getBucharestDateKey());
    // Expirat → butonul arată „Repornește”: re-salvăm activ (data de final expirată se scoate).
    await setLoyaltyModuleActiveFromModClient(active && expired ? true : !active);
  });
}

export async function setEventModuleActiveFromModClient(enabled, opts = {}) {
  let s = getCurrentSettings() || {};
  // data trecută: refuzăm ÎNAINTE să oprim altă activitate. Dacă formularul are modificări nesalvate,
  // contează data din formular (se salvează mai jos), nu cea veche din setări.
  const formDateEl = document.getElementById('eventModuleDate');
  const pendingDate = formDateEl && eventFormHasUnsavedChanges()
    ? String(formDateEl.value || '').trim()
    : String(s.eventModule?.eventDate || '').trim();
  if (enabled && pendingDate && pendingDate < getBucharestDateKey()) {
    showSnackbar('Data evenimentului a trecut. Pune o dată nouă (sau șterge-o) și salvează, apoi activează.', 'error');
    return;
  }
  if (enabled && getModuleActivateBlockReason('evenimente', s)) {
    const free = await resolveModuleConflict('evenimente', s);
    if (!free) return;
    s = getCurrentSettings() || {};
  }
  if (enabled && eventFormHasUnsavedChanges()) {
    try {
      await saveEventModuleSettings({ quiet: true });
    } catch (e) {
      showSnackbar(e?.message || 'Nu am putut salva modificările.', 'error');
      if (opts.quiet) throw e;
      return;
    }
    s = getCurrentSettings() || {};
  }
  const em = s.eventModule || {};
  if (enabled && em.eventDate && String(em.eventDate) < getBucharestDateKey()) {
    showSnackbar('Data evenimentului a trecut. Pune o dată nouă (sau șterge-o) și salvează, apoi activează.', 'error');
    updateModClientModulesUI(getCurrentSettings());
    return;
  }
  const toSave = {
    eventModule: {
      enabled: !!enabled,
      buttonLabel: String(em.buttonLabel || '').trim() || DEFAULT_EVENT_BUTTON_LABEL,
      title: String(em.title || '').trim() || DEFAULT_EVENT_TITLE,
      text: String(em.text || '').trim().slice(0, 2000),
      images: Array.isArray(em.images) ? em.images : [],
      eventDate: String(em.eventDate || '').trim(),
      eventTime: String(em.eventTime || '').trim()
    }
  };

  try {
    await saveCampaignSettingsBackend(toSave);
    setCurrentSettings({ ...s, eventModule: toSave.eventModule });

    const { applyClientUI } = await import('./slideshow.js?v=83.21');
    applyClientUI();
    updateModClientModulesUI(getCurrentSettings());
    await refreshAllAdminSummaries();

    if (!opts.quiet) {
      showSnackbar(
        enabled ? 'Modul evenimente activ pe client.' : 'Modul evenimente dezactivat.',
        'success'
      );
    }
  } catch (e) {
    showSnackbar(e?.message || 'Nu am putut actualiza modulul Evenimente.', 'error');
    updateModClientModulesUI(getCurrentSettings());
    if (opts.quiet) throw e;
  }
}

export async function toggleModClientEvenimente() {
  await withModClientModuleToggleBusy('evenimente', async () => {
    await refreshModClientModulesAsync();
    const s = getCurrentSettings();
    if (!s) return;
    const { enabled } = getEventState(s);
    await setEventModuleActiveFromModClient(!enabled);
  });
}

export function handleModClientModuleToggle(moduleId) {
  const id = String(moduleId || '').toLowerCase();
  if (!MODULE_IDS.includes(id)) return Promise.resolve();
  if (id === 'campanii') return toggleModClientCampanii();
  if (id === 'fidelizare') return toggleModClientFidelizare();
  if (id === 'evenimente') return toggleModClientEvenimente();
  return Promise.resolve();
}

/** Reîncarcă fidelizarea din Firestore apoi actualizează cardurile Mod Client. */
export async function refreshModClientModulesAsync() {
  try {
    const { getPublicClientSlug } = await import('../services/public-client.js?v=83.21');
    if (getPublicClientSlug()) {
      updateModClientModulesUI(getCurrentSettings());
      return;
    }
    const { ensureLoyaltySettingsLoaded } = await import('../services/loyalty.js?v=83.21');
    await ensureLoyaltySettingsLoaded(true);
    updateModClientModulesUI(getCurrentSettings());
  } catch (_) {
    /* ignore */
  }
}

/** @deprecated — folosit de admin-collapsible */
export function updateModClientCardInfo(settings) {
  updateModClientModulesUI(settings);
  refreshModClientModulesAsync();
}
